import React, { useMemo } from "react";
import { Box, Button, Chip, Stack, Typography, useTheme } from "@mui/material";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ToolResultRendererProps } from "./index";
import { CodeComponent } from "src/reusable/Chat/AssistantMessage/AssistantText";
import {
  containsRenderableHtml,
  sanitizeHtml,
  htmlContentStyles,
} from "src/views/assistant/utils/htmlContentUtils";

const contentStyles = {
  ...htmlContentStyles,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

interface ContentItem {
  content: string;
  source_file_name?: string;
  source_data_record_id?: number;
  dataset_id?: number;
  metadata?: {
    document_title?: string;
    document_author?: string;
    document_type?: string;
    page_number?: number | number[];
    publication_date?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

function getSourceName(item: ContentItem): string | null {
  if (item.source_file_name) return item.source_file_name;
  if (
    item.metadata?.document_title &&
    item.metadata.document_title !== "Unknown"
  ) {
    return item.metadata.document_title;
  }
  return null;
}

function getPageNumbers(item: ContentItem): number[] | null {
  const pageNum = item.metadata?.page_number;
  if (pageNum === undefined || pageNum === null) return null;
  // Backend stores 1-indexed page numbers (page_idx + 1), no conversion needed
  if (Array.isArray(pageNum) && pageNum.length > 0) {
    return pageNum;
  }
  return [pageNum as number];
}

interface ResultCardProps {
  item: ContentItem;
}

const ResultCard: React.FC<ResultCardProps> = ({ item }) => {
  const theme = useTheme();
  const content = item.content || "";

  // Determine if content contains HTML
  const { hasHtml, sanitizedContent } = useMemo(() => {
    if (!content) return { hasHtml: false, sanitizedContent: "" };
    if (containsRenderableHtml(content)) {
      const sanitized = sanitizeHtml(content);
      return { hasHtml: true, sanitizedContent: sanitized };
    }
    return { hasHtml: false, sanitizedContent: "" };
  }, [content]);

  const sourceName = getSourceName(item);
  const pageNumbers = getPageNumbers(item);
  const docType = item.metadata?.document_type as string | undefined;
  const docTitle = item.metadata?.document_title as string | undefined;
  const author = item.metadata?.document_author as string | undefined;
  const pubDate = item.metadata?.publication_date as string | undefined;
  const sdrId = item.source_data_record_id;
  const datasetId = item.dataset_id;

  // Format page numbers for display
  const pageString = pageNumbers
    ? pageNumbers.length > 1
      ? `${pageNumbers[0]}-${pageNumbers[pageNumbers.length - 1]}`
      : String(pageNumbers[0])
    : null;

  // Build document URL if we have the required IDs
  // pageNumbers is already 1-indexed from getPageNumbers()
  const documentUrl =
    datasetId && sdrId
      ? `/datasets/${datasetId}/document/${sdrId}${
          pageNumbers ? `?page_number=${pageNumbers[0]}` : ""
        }`
      : null;

  const handleViewPage = (e: React.MouseEvent) => {
    e.preventDefault();
    if (documentUrl) {
      window.open(documentUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Box
      sx={{
        borderRadius: "8px",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        mb: 1.5,
        "&:last-child": { mb: 0 },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: "#f5f5f5",
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          flexWrap="wrap"
        >
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            {docType && docType !== "Unknown" && (
              <Chip label={docType} size="small" variant="outlined" />
            )}
            <Typography
              variant="body2"
              sx={{ fontWeight: "bold", wordBreak: "break-all" }}
            >
              {sourceName || "Search Result"}
              {pageString && ` - Page ${pageString}`}
            </Typography>
          </Stack>
          {documentUrl && (
            <Button variant="contained" size="small" onClick={handleViewPage}>
              View Page
            </Button>
          )}
        </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2.5, bgcolor: "#ffffff" }}>
        {hasHtml ? (
          <Box
            sx={{ overflowX: "auto", ...contentStyles }}
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />
        ) : (
          <Box sx={contentStyles}>
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={{ code: CodeComponent }}
            >
              {content}
            </Markdown>
          </Box>
        )}
      </Box>

      {/* Footer - metadata grid like DocumentDataCard */}
      <Box
        sx={{
          bgcolor: "#fafafa",
          px: 2.5,
          py: 1.75,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
            fontSize: "0.813rem",
          }}
        >
          {/* Document Title - full width */}
          {docTitle && docTitle !== "Unknown" && (
            <Box sx={{ color: "#333", gridColumn: "1 / -1" }}>
              <strong>Document Title:</strong>{" "}
              <span style={{ color: "#666" }}>{docTitle}</span>
            </Box>
          )}
          {/* Author */}
          {author && author !== "Unknown" && (
            <Box sx={{ color: "#333" }}>
              <strong>Author:</strong>{" "}
              <span style={{ color: "#666" }}>{author}</span>
            </Box>
          )}
          {/* Publication Date from metadata */}
          {pubDate && (
            <Box sx={{ color: "#333" }}>
              <strong>Publication Date:</strong>{" "}
              <span style={{ color: "#666" }}>{pubDate}</span>
            </Box>
          )}
          {/* File Name */}
          {sourceName && (
            <Box sx={{ color: "#333" }}>
              <strong>File Name:</strong>{" "}
              <span style={{ color: "#666" }}>{sourceName}</span>
            </Box>
          )}
          {/* Page(s) */}
          {pageNumbers && pageNumbers.length > 0 && (
            <Box sx={{ color: "#333" }}>
              <strong>Page(s):</strong>{" "}
              <span style={{ color: "#666" }}>{pageString}</span>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const ContentArrayRenderer: React.FC<ToolResultRendererProps> = ({
  data,
}) => {
  const items = data as ContentItem[];

  if (!Array.isArray(items) || items.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No results
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      {items.map((item, index) => (
        <ResultCard key={index} item={item} />
      ))}
    </Box>
  );
};
